import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import type { ReactNode } from 'react';
import type { CopyPriceItem } from './CopyProductsAndPackagesStep';

type SelectOption = { id: string; label: string; value: string };

type FinalizeFormValues = {
  organizationName: string;
  organizationTypeId: string;
  telephone: string;
  email: string;
  website: string;
  street: string;
  city: string;
  country: string;
  postalCode: string;
  timeZone: string;
  firstName: string;
  lastName: string;
  designation: string;
  language: string;
  contactTelephone: string;
  contactEmail: string;
};

const EMPTY = '—';

const formatMsrp = (value: string): string => {
  if (!value.trim()) return EMPTY;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return value;
  return parsed.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const displayValue = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : EMPTY;
};

const PreviewField = ({ label, value }: { label: string; value?: string | null }) => {
  const theme = useTheme();

  return (
    <Box>
      <Typography
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: theme.palette.text.secondary,
          letterSpacing: 0.3,
          marginBottom: 4,
        }}>
        {label}
      </Typography>
      <Typography
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: theme.palette.text.primary,
          wordBreak: 'break-word',
          lineHeight: 1.4,
        }}>
        {displayValue(value)}
      </Typography>
    </Box>
  );
};

const PreviewCard = ({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) => {
  const theme = useTheme();

  return (
    <Box
      style={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: 16,
        minWidth: 0,
      }}>
      <Box display="flex" alignItems="center" style={{ gap: 10, marginBottom: 14 }}>
        <Box
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
          {icon}
        </Box>
        <Typography
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
};

const ProductRow = ({ item }: { item: CopyPriceItem }) => {
  const theme = useTheme();

  return (
    <Box
      display="flex"
      alignItems="center"
      style={{
        gap: 12,
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: '8px 12px',
      }}>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Typography
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: theme.palette.text.primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {item.name}
        </Typography>
        {item.sku ? (
          <Typography
            title={item.sku}
            style={{
              fontSize: 11,
              fontStyle: 'italic',
              color: theme.palette.text.disabled,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {item.sku}
          </Typography>
        ) : null}
      </Box>
      <Box display="flex" style={{ gap: 16, flexShrink: 0 }}>
        {item.showMonthly && (
          <Box style={{ minWidth: 88, textAlign: 'right' }}>
            <Typography
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: theme.palette.text.secondary,
              }}>
              Monthly
            </Typography>
            <Typography
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}>
              {formatMsrp(item.monthlyMsrp)}
            </Typography>
          </Box>
        )}
        {item.showOneYear && (
          <Box style={{ minWidth: 88, textAlign: 'right' }}>
            <Typography
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: theme.palette.text.secondary,
              }}>
              1-Year
            </Typography>
            <Typography
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}>
              {formatMsrp(item.oneYearMsrp)}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ProductGroup = ({ title, items }: { title: string; items: CopyPriceItem[] }) => {
  const theme = useTheme();

  if (items.length === 0) return null;

  return (
    <Box>
      <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
        <Typography
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}>
          {title}
        </Typography>
        <Box
          component="span"
          style={{
            minWidth: 20,
            height: 18,
            padding: '0 6px',
            borderRadius: 9,
            backgroundColor: theme.palette.grey[200],
            color: theme.palette.text.secondary,
            fontSize: 11,
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          {items.length}
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
        {items.map((item) => (
          <ProductRow key={item.key} item={item} />
        ))}
      </Box>
    </Box>
  );
};

interface FinalizeStepProps {
  values: FinalizeFormValues;
  organizationTypeOptions: SelectOption[];
  languageOptions: SelectOption[];
  countryLabelByValue: Record<string, string>;
  copyItems: CopyPriceItem[];
}

export const FinalizeStep = ({
  values,
  organizationTypeOptions,
  languageOptions,
  countryLabelByValue,
  copyItems,
}: FinalizeStepProps) => {
  const theme = useTheme();
  const selectedItems = copyItems.filter((item) => item.selected);
  const packages = selectedItems.filter((item) => item.entityType === 'PACKAGE');
  const products = selectedItems.filter((item) => item.entityType === 'PRODUCT');
  const organizationTypeLabel =
    organizationTypeOptions.find((option) => option.value === values.organizationTypeId)
      ?.label ?? values.organizationTypeId;
  const languageLabel =
    languageOptions.find((option) => option.value === values.language)?.label ??
    values.language;
  const countryLabel = countryLabelByValue[values.country] || values.country;
  const iconSx = { color: theme.palette.primary.main, fontSize: 18 };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 12, marginBottom: 8 }}>
      <Box display="grid" gridTemplateColumns="1fr 1fr" style={{ gap: 12 }}>
        <PreviewCard
          title="Company Details"
          icon={<BusinessOutlinedIcon style={iconSx} />}>
          <Box display="grid" gridTemplateColumns="1fr 1fr" style={{ gap: 12 }}>
            <PreviewField label="COMPANY NAME" value={values.organizationName} />
            <PreviewField label="COMPANY TYPE" value={organizationTypeLabel} />
            <PreviewField label="TELEPHONE" value={values.telephone} />
            <PreviewField label="EMAIL" value={values.email} />
            <PreviewField label="WEBSITE" value={values.website} />
            <PreviewField label="COUNTRY" value={countryLabel} />
            <PreviewField label="CITY" value={values.city} />
            <PreviewField label="STREET" value={values.street} />
            <PreviewField label="POSTAL CODE" value={values.postalCode} />
            <PreviewField label="TIME ZONE" value={values.timeZone} />
          </Box>
        </PreviewCard>

        <PreviewCard
          title="User Details"
          icon={<PersonOutlineIcon style={iconSx} />}>
          <Box display="grid" gridTemplateColumns="1fr 1fr" style={{ gap: 12 }}>
            <PreviewField label="FIRST NAME" value={values.firstName} />
            <PreviewField label="LAST NAME" value={values.lastName} />
            <PreviewField label="DESIGNATION" value={values.designation} />
            <PreviewField label="LANGUAGE" value={languageLabel} />
            <PreviewField label="TELEPHONE" value={values.contactTelephone} />
            <PreviewField label="EMAIL" value={values.contactEmail} />
          </Box>
        </PreviewCard>
      </Box>

      <PreviewCard
        title={`Products & Packages${
          selectedItems.length > 0 ? ` (${selectedItems.length})` : ''
        }`}
        icon={<Inventory2OutlinedIcon style={iconSx} />}>
        {selectedItems.length === 0 ? (
          <Typography
            style={{
              fontSize: 13,
              color: theme.palette.text.secondary,
            }}>
            No products or packages selected. The client will be created without copying a
            price book.
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" style={{ gap: 14 }}>
            <ProductGroup title="Packages" items={packages} />
            <ProductGroup title="Products" items={products} />
          </Box>
        )}
      </PreviewCard>
    </Box>
  );
};
